import React, { useEffect } from 'react'
import className from 'classnames/bind'

import style from './MyhouseIntroducePage.module.scss'

import ImgTitle from '@Ui/images/myhouse/introduce/ImgTitle.svg'
import ImgDescription from '@Ui/images/myhouse/introduce/ImgDescription.svg'
import ImgSubtitle1 from '@Ui/images/myhouse/introduce/ImgSubtitle1.svg'
import ImgSubtitle2 from '@Ui/images/myhouse/introduce/ImgSubtitle2.svg'
import ImgSubtitle3 from '@Ui/images/myhouse/introduce/ImgSubtitle3.svg'
import ImgSubtitle4 from '@Ui/images/myhouse/introduce/ImgSubtitle4.svg'
import ImgChart from '@Ui/images/myhouse/introduce/ImgChart.svg'
import ImgReview from '@Ui/images/myhouse/introduce/ImgReview.svg'
import ImgNotice from '@Ui/images/myhouse/introduce/ImgNotice.svg'
import ImgMyAsset from '@Ui/images/myhouse/introduce/ImgMyAsset.svg'
import LogoNaverFinancial_160x12 from '@Ui/images/myhouse/introduce/LogoNaverFinancial_160x12.svg'
import MyhouseIntroduceHeader from '@Components/atoms/MyhouseIntroduceHeader'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import useMyhouseRegister from '@Hooks/myhouse/useMyhouseRegister'
import { FETCH_STATUS } from '@Root/contants'
import useUser from '@Hooks/user/useUser'
import FloatingButton from '@Components/atoms/FloatingButton'
import InsuranceLayout from '@Components/atoms/InsuranceLayout'
import InsurancePromotionLayout from '@Components/atoms/InsurancePromotionLayout'

const cx = className.bind(style)

const MyhouseIntroducePage: React.FC = () => {
  const { myhouseRegister, loadMyHouseExist } = useMyhouseRegister()
  const { myHouse } = myhouseRegister
  const { user } = useUser()

  function onClickGoAddressSearchPage(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    if (user.isLogin) {
      window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE_REGISTER}/address`
    } else {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}&url=${process.env.REACT_APP_LAND_WEB_URL}/myhouse/introduce`
    }
  }

  const handleMyHouseMainClick = (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (user.isLogin) {
      window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}`
    } else {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}&url=${process.env.REACT_APP_LAND_WEB_URL}/myhouse/introduce`
    }
  }

  useEffect(() => {
    loadMyHouseExist()
  }, [])

  if (myHouse.fetchStatus === FETCH_STATUS.INITIAL || myHouse.fetchStatus === FETCH_STATUS.PENDING) {
    return null
  }

  return (
    <>
      <MyhouseIntroduceHeader />
      <InsurancePromotionLayout>
        <InsuranceLayout>
          <div className={cx('article')}>
            <div className={cx('wrap_title')}>
              <h2 className={cx('title')}>
                <ImgTitle />
                <span className="blind">한 눈에 확인하는 우리집</span>
              </h2>
              <p className={cx('description')}>
                <ImgDescription />
                <span className="blind">시세/실거래가·호가·단지 공지사항/리뷰 등 흩어져 있는 우리집 정보들! 이제 네이버페이 부동산 우리집에서 간편하게 확인하세요.</span>
              </p>
            </div>
            <ol className={cx('list')}>
              <li className={cx('item')}>
                <span className={cx('number')}>1</span>
                <h3 className={cx('subtitle')}>
                  <ImgSubtitle1 />
                  <span className="blind">알면 알수록 부자되는 우리집 자산가치 정보</span>
                </h3>
                <div className={cx('image')}>
                  <ImgChart />
                </div>
                <em className={cx('emphasis')}>시세 ・ 실거래가 ・ 호가</em>
                <p className={cx('detail')}>
                  가치 판단에 도움되는 알짜 정보만 모았어요.
                  <br />
                  집주인을 인증하면 동호수별 상세 시세도 볼 수 있답니다.
                  <br />
                  모바일 알림 설정하고 변동 정보도 놓치지 마세요.
                </p>
              </li>
              <li className={cx('item')}>
                <span className={cx('number')}>2</span>
                <h3 className={cx('subtitle')}>
                  <ImgSubtitle2 />
                  <span className="blind">입주민이 직접 작성하는 우리단지 리뷰</span>
                </h3>
                <div className={cx('image')}>
                  <ImgReview />
                </div>
                <em className={cx('emphasis')}>“이 아파트의 거주 만족도는 어떨까?”</em>
                <p className={cx('detail')}>
                  평소 궁금했던 단지들의 입주민 리뷰를 확인하고
                  <br />
                  우리단지 리뷰도 작성해 보세요. 우리단지 리뷰는
                  <br />
                  외부인들에게 좋은 홍보 수단이 될 수 있어요.
                </p>
              </li>
              <li className={cx('item')}>
                <span className={cx('number')}>3</span>
                <h3 className={cx('subtitle')}>
                  <ImgSubtitle3 />
                  <span className="blind">모바일로 확인하는 우리단지 공지사항</span>
                </h3>
                <div className={cx('image')}>
                  <ImgNotice />
                </div>
                <em className={cx('emphasis')}>이웃과 나누는 우리단지 최신 정보</em>
                <p className={cx('detail')}>
                  단지 공지사항과 동네 소식을 등록해 보세요.
                  <br />
                  이웃 주민들에게 유용한 정보가 될 거예요.
                </p>
              </li>
              <li className={cx('item')}>
                <span className={cx('number')}>4</span>
                <h3 className={cx('subtitle')}>
                  <ImgSubtitle4 />
                  <span className="blind">네이버페이 내 자산에서 바로보는 우리집</span>
                </h3>
                <div className={cx('image', 'my-asset')}>
                  <ImgMyAsset />
                </div>
                <em className={cx('emphasis')}>부동산을 비롯한 내 자산 관리 서비스</em>
                <p className={cx('detail')}>
                  네이버페이 내 자산에 우리집이 자동 연동되어
                  <br />
                  부동산 자산이 합산된 내 자산 합계를
                  <br />
                  편리하게 확인하고 관리할 수 있어요.
                </p>
              </li>
            </ol>
            <div className={cx('logo_naver')}>
              <LogoNaverFinancial_160x12 />
              <span className="blind">NAVER FINANCIAL</span>
            </div>
            <FloatingButton>
              <FloatingButton.GrayButton label="우리집 바로가기" onClick={handleMyHouseMainClick} />
              <FloatingButton.GreenButton label="우리집 등록하기" onClick={onClickGoAddressSearchPage} />
            </FloatingButton>
          </div>
        </InsuranceLayout>
      </InsurancePromotionLayout>
      <div id="naver-splugin-wrap" style={{ display: 'none' }} />
    </>
  )
}

export default MyhouseIntroducePage
